<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <div class="p-4 border-b border-gray-200">
      <h1 class="text-xl font-bold mb-3">Conversas</h1>
      
      <!-- Search -->
      <div class="relative">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Buscar conversas..."
          class="w-full px-4 py-2 border border-gray-300 rounded-lg pl-10 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <MagnifyingGlassIcon class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
      </div>
      
      <!-- Filters -->
      <div class="flex gap-2 mt-3 flex-wrap">
        <button
          v-for="filter in filters"
          :key="filter.value"
          @click="activeFilter = filter.value"
          :class="[
            'px-3 py-1.5 rounded-full text-sm font-medium transition',
            activeFilter === filter.value
              ? 'bg-primary-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]"
        >
          {{ filter.label }}
          <span v-if="filter.count" class="ml-1">({{ filter.count }})</span>
        </button>
      </div>
    </div>
    
    <!-- Conversation List -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="loading" class="flex items-center justify-center h-32">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
      
      <div v-else-if="filteredConversations.length === 0" class="text-center py-12 text-gray-400">
        <ChatBubbleLeftRightIcon class="w-16 h-16 mx-auto mb-3 text-gray-300" />
        <p>Nenhuma conversa encontrada</p>
      </div>
      
      <ConversationCard
        v-for="conversation in filteredConversations"
        :key="conversation.id"
        :conversation="conversation"
        :active="conversation.id === activeConversationId"
        @click="selectConversation(conversation.id)"
        @refresh="refreshList"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { MagnifyingGlassIcon, ChatBubbleLeftRightIcon } from '@heroicons/vue/24/outline'
import { useConversationsStore } from '@/stores/conversations'
import { useUserStore } from '@/stores/user'
import ConversationCard from './ConversationCard.vue'

const conversationsStore = useConversationsStore()
const userStore = useUserStore()

const searchQuery = ref('')
const activeFilter = ref('all')

const filters = computed(() => [
  { 
    label: 'Não lidas', 
    value: 'unread', 
    count: conversationsStore.unreadCount
  },
  { 
    label: 'Minhas', 
    value: 'mine' 
  },
  { 
    label: 'Todas', 
    value: 'all' 
  },
  {
    label: 'Arquivadas',
    value: 'archived'
  }
])

// Watch filter changes to fetch data from backend when needed
import { watch } from 'vue'

watch(activeFilter, async (newVal, oldVal) => {
  // If switching TO 'archived', fetch archived conversations
  if (newVal === 'archived') {
    await conversationsStore.fetchConversations({ status: 'archived' })
  } 
  // If switching FROM 'archived' TO something else, fetch default (open) conversations
  else if (oldVal === 'archived') {
    await conversationsStore.fetchConversations()
  }
  // Switching between 'all', 'unread', 'mine' uses client-side filtering on the default set,
  // so no new fetch is needed (unless we want to support server-side pagination later).
})

const filteredConversations = computed(() => {
  let conversations = conversationsStore.conversations || []
  
  // Apply filter
  if (activeFilter.value === 'unread') {
    conversations = conversations.filter(c => c.unread_count > 0)
  } else if (activeFilter.value === 'mine') {
    const currentUserId = userStore.currentUser?.id
    conversations = conversations.filter(c => c.assigned_to === currentUserId)
  }
  // 'all' and 'archived' pass through here without filtering array
  // (because store already has the correct dataset)
  
  // Apply search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    conversations = conversations.filter(c => 
      c.contact_name?.toLowerCase().includes(query) ||
      c.contact_phone?.includes(query)
    )
  }
  
  return conversations
})

const activeConversationId = computed(() => conversationsStore.activeConversationId)
const loading = computed(() => conversationsStore.loading)

function selectConversation(id) {
  conversationsStore.setActiveConversation(id)
}

function refreshList() {
  // Reload current view based on filter
  if (activeFilter.value === 'archived') {
    conversationsStore.fetchConversations({ status: 'archived' })
  } else {
    conversationsStore.fetchConversations()
  }
}
</script>
